import { nanoid } from 'nanoid';
import { IForm } from 'pages/revisions-module/template-editor/editors/form/types';
import {
  DeviceFields,
  RevisionFormData as NewRevisionFormData,
  RevisionDevices as NewRevisionDevices,
} from 'components/forms/revision/fill-out/types';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { resetMeasurementRowLockMetadata } from 'components/forms/revision/fill-out/modals/device-measurement-modal/measurement-row-lock';

/**
 * Copies persisted revision data onto a new revision while respecting copyable fields.
 */
export function copyRevisionData({
  oldRevision,
  newDeviceIds,
}: {
  oldRevision: RevisionGetResponse;
  newDeviceIds: number[];
}) {
  const { sections }: IForm = JSON.parse(oldRevision.revisionTemplate!.formScheme!);

  const copyableFields = sections.flatMap(({ rows }) =>
    rows.flatMap(({ inputs }) => inputs.filter(({ spec }) => spec.copyable).map(({ spec }) => spec.name))
  );

  const oldData = JSON.parse(oldRevision.revisionData!) as NewRevisionFormData;

  const newFields = Object.keys(oldData.fields)
    .filter((key) => copyableFields.includes(key))
    .reduce((acc, key) => {
      acc[key] = oldData.fields[key];
      return acc;
    }, {} as Record<string, any>);

  const newDevices = Object.entries(oldData.devices)
    .filter(([, device]) => newDeviceIds.includes(device.deviceId))
    .reduce((acc, [key, device]) => {
      const fields = Object.keys(device.fields)
        .filter((fieldKey) => copyableFields.includes(fieldKey))
        .reduce((fieldAcc, fieldKey) => {
          fieldAcc[fieldKey] = device.fields[fieldKey];
          return fieldAcc;
        }, {} as DeviceFields);

      const measurements = Object.entries(device.measurements).reduce(
        (measurementAcc, [measurementKey, measurement]) => {
          measurementAcc[measurementKey] = measurement.map((measurementRow) => {
            const copiedMeasurementRow = Object.keys(measurementRow)
              .filter((rowKey) => rowKey !== '_faults' && (rowKey.startsWith('_') || copyableFields.includes(rowKey)))
              .reduce((rowAcc, rowKey) => {
                rowAcc[rowKey] = measurementRow[rowKey];
                return rowAcc;
              }, {} as Record<string, any>);

            return {
              ...resetMeasurementRowLockMetadata(copiedMeasurementRow),
              _faults: [],
              _id: nanoid(),
            };
          });

          return measurementAcc;
        },
        {} as Record<string, any>
      );

      acc[key] = {
        deviceId: device.deviceId,
        fields,
        faults: [],
        measurements,
      };

      return acc;
    }, {} as NewRevisionDevices);

  return {
    fields: newFields,
    devices: newDevices,
  };
}
